import type { JSX } from 'react';

import { Waypoint } from 'react-waypoint';

import { Text } from '@change/ds-core-ui/next';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useRecommendedGroupPickerContext } from 'src/app/pages/starterDashboardV2/shared/components';
import type {
	FacebookPost,
	FullPostType,
	PostPreviewSizes,
	RecommendedGroup,
	RecommendedGroupPickerProps,
	RedditPost,
	SharingProps,
} from 'src/app/pages/starterDashboardV2/shared/components';
import {
	extractSharingResource,
	mapToSharingModalProps,
	POST_CONFIGS,
	useMarkModuleAsLoaded,
	useReadyToShareContext,
	useRecommendations,
	useSharingModal,
} from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { PostConfigRow } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { ReadyToSharePostWithPlatform } from 'src/app/shared/api/readyToShare';
import type { WaypointCallbackArgs } from 'src/app/shared/components/layout/lazy';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

import { ScrollButtons } from '../shared/ScrollButtons';

import { PlatformFilter } from './components/PlatformFilter';
import { PostPreviewCard } from './components/PostPreviewCard';
import { PostPreviewCardSkeleton } from './components/PostPreviewCardSkeleton';
import { usePlatformFilter, useScroll, useViewTracking } from './hooks';

const trackingProps = {
	context: 'petition_dashboard_ready_to_share_post' as const,
	viewContext: 'share' as const,
};

function recommendedGroupForPost(
	fullPostType: FullPostType,
	postConfig: PostConfigRow,
	recommendationsData: ReturnType<typeof useRecommendations>['data'],
) {
	if (!postConfig.requiresRecommendedGroup) return undefined;

	const { isLoading: isRecommendationsLoading, hasFacebookGroups, hasRedditGroups } = recommendationsData;

	if (isRecommendationsLoading) return undefined;

	if (fullPostType.startsWith('facebook')) {
		return hasFacebookGroups ? recommendationsData.facebook[0] : undefined;
	}

	return hasRedditGroups ? recommendationsData.reddit[0] : undefined;
}

type RenderPostOrStoryProps = {
	post: ReadyToSharePostWithPlatform;
	fullPostType: FullPostType;
	isMobile: boolean;
	petition: StarterDashboardV2PageData['petition'];
	options: {
		previewSize: PostPreviewSizes;
		startSharingFlow: (props: SharingProps) => void;
		startGroupSelection: (
			props: Omit<RecommendedGroupPickerProps, 'isOpen' | 'onGroupSelected' | 'onClose'>,
		) => Promise<RecommendedGroup | undefined>;
	};
	recommendationsData: ReturnType<typeof useRecommendations>['data'];
};

function renderPostOrStory(props: RenderPostOrStoryProps) {
	const {
		post,
		fullPostType,
		isMobile,
		petition,
		recommendationsData,
		options: { startSharingFlow, startGroupSelection, previewSize },
	} = props;

	const postConfig = POST_CONFIGS[fullPostType];
	const postDisabled = !isMobile && !postConfig.desktopEnabled;
	const recommendedGroup = recommendedGroupForPost(fullPostType, postConfig, recommendationsData);

	const sharingResource = extractSharingResource(fullPostType, {
		post,
		petition,
		recommendedGroup,
	});

	const sharingProps = mapToSharingModalProps(petition, {
		fullPostType,
		recommendedGroup,
		trackingProps,
	});

	const handlePostClick = !postDisabled
		? async () => {
				if (postConfig.requiresGroupSelection) {
					const sharingPlatform = sharingProps.platform;
					const recommendedGroupsList =
						sharingPlatform === 'facebook' ? recommendationsData.facebook : recommendationsData.reddit;
					const selectedGroup = await startGroupSelection({
						platform: sharingProps.platform === 'facebook' ? ('facebook' as const) : ('reddit' as const),
						recommendedGroups: recommendedGroupsList,
					});

					if (selectedGroup) {
						startSharingFlow({ ...sharingProps, recommendedGroup: selectedGroup } as RedditPost | FacebookPost);
					}
				} else {
					startSharingFlow(sharingProps);
				}
			}
		: () => {};

	return (
		<PostPreviewCard
			key={`${fullPostType}-post-preview-card`}
			onClick={handlePostClick}
			fullPostType={fullPostType}
			disabled={postDisabled}
			petition={petition}
			sharingResource={sharingResource}
			previewSize={previewSize}
		/>
	);
}

export function ReadyToSharePreDrafted(): JSX.Element | null {
	const isMobile = useIsMobile();
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const { data: contextData } = useReadyToShareContext();
	const { data: recommendationsData } = useRecommendations();

	const { startGroupSelection } = useRecommendedGroupPickerContext();
	const { startSharingFlow } = useSharingModal();

	const {
		data: { selectedPlatform, hasAnyValidContent, availablePlatforms, displayablePosts },
		actions: { selectPlatform, clearPlatformSelection },
	} = usePlatformFilter(contextData.platformData, recommendationsData);

	const {
		data: { canScrollLeft, canScrollRight, scrollContainerRef },
		actions: { handleScroll },
	} = useScroll(selectedPlatform);
	const { trackView } = useViewTracking();

	useMarkModuleAsLoaded('ReadyToSharePreDrafted', contextData.shouldShowReadyToShareExperiment);

	if (!contextData.shouldShowReadyToShareExperiment) {
		return null;
	}

	// Hide module if no valid content is available and not loading
	if (!hasAnyValidContent) {
		return null;
	}

	const previewSize = (isMobile ? 'medium' : 'large') as PostPreviewSizes;

	const baseRenderPostProps = {
		petition,
		isMobile,
		recommendationsData,
		options: {
			previewSize,
			startSharingFlow,
			startGroupSelection,
		},
	};

	return (
		<section className="bg-background-base flex h-full w-full flex-col px-4 py-8 md:px-12 md:pb-12 md:pt-8">
			<Waypoint onPositionChange={(wp: WaypointCallbackArgs) => trackView(wp.currentPosition)} />

			<div className="mb-5">
				<Text className="text-size-heading-xs md:text-size-heading-small">Ready-to-share posts</Text>
			</div>
			<div className="flex justify-between align-middle">
				<PlatformFilter
					selectedPlatform={selectedPlatform}
					onClearSelection={clearPlatformSelection}
					onPlatformSelect={selectPlatform}
					availablePlatforms={availablePlatforms}
				/>
				{/* Scroll Buttons */}
				<ScrollButtons
					canScrollLeft={canScrollLeft}
					canScrollRight={canScrollRight}
					onScrollLeft={() => handleScroll('left')}
					onScrollRight={() => handleScroll('right')}
				/>
			</div>

			{/* Posts Container with Horizontal Scrolling */}
			<div
				className="flex w-full flex-row overflow-x-auto px-1 pb-3 pt-6 sm:gap-6 md:gap-x-[55px]"
				ref={scrollContainerRef}
				sx={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
			>
				{/* Collect all posts and stories from all platforms */}
				{displayablePosts.map((displayablePost) => {
					const { post, fullPostType } = displayablePost;

					if (post) {
						return renderPostOrStory({ ...baseRenderPostProps, post, fullPostType });
					}

					return (
						<PostPreviewCardSkeleton
							key={`${fullPostType}-post-skeleton`}
							size={previewSize}
							petition={petition}
							fullPostType={fullPostType}
						/>
					);
				})}
			</div>
		</section>
	);
}
